import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { string, object, ref, array } from "yup";

const domainRegex =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?::[1-9][0-9]{0,4})?$/;

const confirmPasswordSchema = toTypedSchema(
  object({
    password: string()
      .required("Password is required")
      .min(12, "Password must be at least 12 characters long")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W)./,
        "Password must contain at least one lowercase letter, one uppercase and one symbol.",
      ),
    confirmPassword: string()
      .required("Confirm Password is required")
      .oneOf([ref("password"), null], "Passwords must match"),
  }),
);

const systemSchema = maxDomains =>
  toTypedSchema(
    object({
      name: string().trim().required("Application name is required"),
      domain: string()
        .trim()
        .matches(domainRegex, { message: "Domain is invalid", excludeEmptyString: true })
        .required("Domain is required"),
      domainList: array()
        .of(
          string().matches(domainRegex, {
            message: "One or more domains are invalid",
            excludeEmptyString: true,
          }),
        )
        .max(
          maxDomains,
          `You have reached the limit of ${maxDomains} per Application. Contact the Customer Success team for details.`,
        )
        .optional(),
    }),
  );

/**
 * Creates a vee-validate form for the System settings card.
 *
 * Form values keep raw input as typed; whitespace trimming happens on save
 * (`fillModel`). The `.trim()` transforms affect validation and initial-value
 * normalization only.
 *
 * @param {{ name: string, domain: string, domainList: string[] | null }} initialValues - current app values; a missing/null domainList is normalized to an empty array.
 * @param {number} maxDomains - maximum allowed domain aliases.
 * @returns {{ values: { name: Ref<string>, domain: Ref<string>, domainList: Ref<string[]> }, props: { name: Ref, domain: Ref, domainList: Ref }, errors: ComputedRef<Record<string, string>>, meta: ComputedRef<{ valid: boolean, dirty: boolean }>, validate: () => Promise<{ valid: boolean, errors: Record<string, string> }> }} vee-validate form API.
 */
export const useFormSystem = (initialValues, maxDomains) => {
  const { errors, defineField, meta, validate } = useForm({
    validationSchema: systemSchema(maxDomains),
    initialValues: { ...initialValues, domainList: initialValues.domainList ?? [] },
  });

  const [name, nameProps] = defineField("name");
  const [domain, domainProps] = defineField("domain");
  const [domainList, domainListProps] = defineField("domainList");

  return {
    values: { name, domain, domainList },
    props: { name: nameProps, domain: domainProps, domainList: domainListProps },
    errors,
    meta,
    validate,
  };
};

export const useFormPasswords = () => {
  const { errors, defineField, meta } = useForm({ validationSchema: confirmPasswordSchema });

  const [password, passwordProps] = defineField("password");
  const [confirmPassword, confirmPasswordProps] = defineField("confirmPassword");
  return {
    values: { password, confirmPassword },
    props: { password: passwordProps, confirmPassword: confirmPasswordProps },
    errors,
    meta,
  };
};
